import { getUserData } from "@/auth/getUserData";
// @ts-ignore
import useAws from "@/aws/useAws";
import { useCookies } from "@/composables/useCookies";
import SignedUrlInterface from "@/interfaces/signedUrl.interface";
import axios from "@/libs/axios";
// @ts-ignore
import i18n from "@/libs/i18n";
// @ts-ignore
import luxon from "@/libs/luxon";
// @ts-ignore
import { diff } from "@/utils/luxon";
import { Asset } from "@saysimple-platform/dtos";

export const shouldRenewSignedCookies = (): boolean => {
    const { state } = useCookies();

    const toExpire = diff(state.signedCookiesExpiryDate, luxon.DateTime.now().toUTC());

    // We keep a margin of 1hour. So for example when signed cookies are valid for 24h and we open a chat after
    // 23h and 5m of inactivity, this will return true and we know to refresh the signed cookies.
    return toExpire.milliseconds < 3600000;
};

export const uploadMultiple = (assets: File[]) => {
    return Promise.all(assets.map((asset: File) => uploadSingle(asset)));
};

export const uploadSingle = (asset: File | any) => {
    return new Promise((resolve, reject) => {
        // If the provided asset is not an instance of FormData, we're directly uploading an asset instanceof File.
        if (!(asset instanceof File)) {
            reject(i18n.t("entities.asset.instance-error", { filename: asset.name }));
        }

        if (asset.size > 1024 * 1024 * 5) {
            reject(i18n.t("entities.asset.filesize-error", { filename: asset.name }));
        }

        const formData = new FormData();
        formData.append("file", asset);
        axios
            .post(`/assets/upload/storage-name/${getUserData().storageName}`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            })
            .then((response) => resolve({ ...response.data, originalFileObject: asset }))
            .catch((error) => reject(error));
    });
};

// @ts-ignore
export const getFullPath = (asset, returnOrigin? = false): string | undefined => {
    if (!asset) {
        return;
    }

    if (asset.url && !returnOrigin) {
        return asset.url;
    }

    return `${process.env.VUE_APP_FRONTEND_ASSET_ENDPOINT}/${asset.filePath}${encodeURIComponent(asset.fileName)}`;
};

// Potentially we could use a file.[extension] default here as fallback instead
export const displayFileName = (asset: Asset): string => asset.originalName || asset.fileName;

// @ts-ignore
export const downloadFile = (asset): Promise<void> => {
    // There might be an asset url in the object, but we need to be sure that this is already an signed URL to handle it as signed URL.
    if (asset.url) {
        const assetUrl = new URL(asset.url);
        const isSignedUrl =
            assetUrl.searchParams.has("Expires") &&
            assetUrl.searchParams.has("Key-Pair-Id") &&
            assetUrl.searchParams.has("Signature");

        if (isSignedUrl) {
            return downloadBlob(asset.url).then((downloadedFile) => showDownloadWindow(downloadedFile, asset.fileName));
        }
    }

    return useAws
        .getSignedUrls([getFullPath(asset)])
        .then((signedUrls: SignedUrlInterface[]) =>
            downloadBlob(signedUrls[0].signed).then((downloadedFile) =>
                showDownloadWindow(downloadedFile, asset.fileName)
            )
        );
};

const showDownloadWindow = (downloadedFile, fileName) => {
    const link = document.createElement("a");

    link.href = window.URL.createObjectURL(new Blob([downloadedFile.data]));
    link.target = "_blank";
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
};

export const downloadBlob = (url) => {
    return axios.get(url, {
        responseType: "blob",
    });
};
